import { DataViewQuickFilter } from '@osos/shared/api';
import {
  DataEntityPresenterModel,
  DataTypeEnum,
  DataViewConfigurationClassificationStylesModel,
  DataViewEditSupportedOperationModel,
  DataViewEntriesDiscussionConfiguration,
  DataViewEntryHistoryConfiguration,
  DataViewRelationModel,
  DataViewsPresenterModel,
  DataViewStyle,
  DISCUSSION_DEFAULT_CONFIG,
  EntryActionOperationEnum,
  FieldPresenterModel,
  FieldViewOptionValueTypeEnum,
  HISTORY_DEFAULT_CONFIG,
  LocalizedValueModel,
  TableViewMaxDisplayedValuesConfig,
} from '@osos/shared/types';
import cloneDeep from 'lodash-es/cloneDeep';
import {
  getTreeFilteredFields,
  hasArchivePermission,
  hasCreatePermission,
  hasEditPermission,
  hasInlineEditPermission,
  isCoreRequestEntity,
  isFieldBlock,
  isFieldRepeatable,
  isVirtualJoinerEntity,
  isVirtualProxyEntity,
} from '../../utils';
import {
  KanbanViewUIConfigurationModel,
  MiniCardViewUIConfigurationModel,
  TreeViewUIConfigurationModel,
} from './data-view-ui-configuration.model';

export interface DataViewUIConfiguration {
  entriesViewTypes: EntriesViewTypes;
}

export interface EntriesViewTypes {
  tableView: any;
  badgeView: any;
  kanbanView: KanbanViewUIConfigurationModel;
  calendarView: any;
  // This is optional so we don't have to over write the report and other main config
  timelineView?: TimelineViewUIConfiguration;
  treeView?: TreeViewUIConfigurationModel;
  miniCard?: MiniCardViewUIConfigurationModel;
}

export interface EntriesViewTypesUIConfiguration {
  display: boolean;
}

export interface UiPageContent {
  viewEntry: boolean;
  viewJson: boolean;
  addEntry: boolean;
  editEntry: boolean;
  showSubheader: boolean;
  archiveEntry: boolean;
  allowFilters: boolean;
  allowFilterCurrentStep: boolean;
  allowFilterPreviousSteps: boolean;
  allowSearch: boolean;
  displayActions: boolean;
  editDataEntity: boolean;
  archiveRecords: boolean;
  numberOfRecords: boolean;
  addToListOfValues: boolean;
  createNewListOfValues: boolean;
  dashboardConfiguration: DashboardConfiguration;
  allowSavedQueries: boolean;
  showFilter: boolean;
  showSearch: boolean;
  showExport: boolean;
}

export class UIConfigurationModel
  implements UiPageContent, DataViewUIConfiguration
{
  entriesViewTypes: EntriesViewTypes;
  viewEntry: boolean;
  viewJson: boolean;
  addEntry: boolean;
  editEntry: boolean;
  showSubheader: boolean;
  showFilter: boolean;
  showMetaDataFilter: boolean;
  showDetailsMetaDataFilter: boolean;
  showCurrentUserFilter: boolean;
  allowChangeSorting: boolean;
  showCurrentUserGroupFilter: boolean;
  showSearch: boolean;
  showExport: boolean;
  archiveEntry: boolean;
  allowFilters: boolean;
  allowFilterCurrentStep: boolean;
  allowFilterPreviousSteps: boolean;
  allowSearch: boolean;
  stateFilter: boolean;
  obsoleteFilter: boolean;
  invalidRequirementFilter: boolean;
  quickFilters?: DataViewQuickFilter[];
  allowSavedQueries: boolean;
  allowUserCollections: boolean;
  orderable: boolean;
  displayActions: boolean;
  editDataEntity: boolean;
  archiveRecords: boolean;
  numberOfRecords: boolean;
  addToListOfValues: boolean;
  showPagination: boolean;
  createNewListOfValues: boolean;
  dashboardConfiguration: DashboardConfiguration;
  uiAttributes?: DataViewStyle;
  entryClassificationStyles?: DataViewConfigurationClassificationStylesModel;
  showTags: boolean;
  showDraftEntries: boolean;
  allowCreateInvalidEntry: boolean;
  allowEditInvalidEntry: boolean;
  allowQuickCreate: boolean;
  allowDuplicateEntry: boolean;
}

export abstract class GeneralUIViewConfiguration extends UIConfigurationModel {
  editableFields: Array<FieldPresenterModel>;
  allowedToEditFields: Array<FieldPresenterModel>;
  constructor(
    protected dataView?: DataViewsPresenterModel,
    protected dataEntity?: DataEntityPresenterModel,
  ) {
    super();
    if (dataView) {
      this.initGeneralConfig(dataView);
      this.editableFields = this.initEditableFields();
      this.allowedToEditFields = this.initAllowedToEditFields();
    } else {
      this.setDefaultGeneralConfig();
      this.editableFields = this.dataEntity.fields;
      this.allowedToEditFields = this.dataEntity.fields;
    }
    this.uiAttributes = this.dataView?.uiAttributes;
  }

  getDataView(): DataViewsPresenterModel {
    return this.dataView;
  }

  initGeneralConfig(dataView: DataViewsPresenterModel): void {
    this.viewEntry = dataView.entriesConfiguration?.enabled || false;
    this.addEntry =
      hasCreatePermission(dataView) &&
      this.dataView?.editConfiguration?.supportedOperations?.some(
        (o) =>
          o.operationType === EntryActionOperationEnum.AllowCreateNewEntries,
      );

    this.showMetaDataFilter = dataView.viewConfiguration?.allowMetaDataFilter;
    this.showDetailsMetaDataFilter =
      dataView.viewConfiguration?.allowDetailsMetaDataFilter;
    this.showCurrentUserFilter =
      dataView.viewConfiguration?.allowCurrentUserFilter;
    this.allowChangeSorting = dataView.viewConfiguration?.allowChangeSorting;
    this.showCurrentUserGroupFilter =
      dataView.viewConfiguration?.allowCurrentUserGroupsFilter;
    this.editEntry = hasEditPermission(dataView);
    this.archiveEntry = hasArchivePermission(dataView);
    this.numberOfRecords = true;
    this.addToListOfValues = false;
    this.archiveRecords = hasArchivePermission(dataView);
    this.createNewListOfValues = false;
    this.editDataEntity = false;
    this.viewJson = false;
    this.allowFilters = dataView.viewConfiguration?.allowFilters;
    this.allowFilterCurrentStep = dataView.viewConfiguration?.allowFilters
      ? dataView.viewConfiguration?.allowFilterCurrentStep
      : false;
    this.allowFilterPreviousSteps = dataView.viewConfiguration?.allowFilters
      ? dataView.viewConfiguration?.allowFilterPreviousSteps
      : false;
    this.allowSearch = dataView.viewConfiguration?.allowSearch;
    this.allowUserCollections =
      dataView.viewConfiguration?.allowUserCollections;
    this.dashboardConfiguration = {
      enabled:
        dataView?.dashboardConfiguration?.enabled &&
        dataView?.dashboardConfiguration?.showEntityOverview,
      addSummery: false,
    };
    this.displayActions = this.editEntry || this.archiveEntry || this.viewEntry;
    this.showSubheader = true;
    this.showPagination = true;
    this.stateFilter = dataView.viewConfiguration?.allowStateFilter;
    this.obsoleteFilter = dataView.viewConfiguration?.allowObsoleteFilter;
    this.invalidRequirementFilter =
      dataView.viewConfiguration?.allowInvalidRequirementsFilter;
    this.quickFilters = dataView.viewConfiguration?.quickFilters;
    this.entryClassificationStyles =
      dataView.viewConfiguration?.entryClassificationStyles;
    this.allowSavedQueries = dataView.viewConfiguration?.allowSavedQueries;
    this.orderable =
      hasEditPermission(dataView) &&
      !isVirtualJoinerEntity(this.dataEntity) &&
      !isVirtualProxyEntity(this.dataEntity) &&
      dataView.viewConfiguration?.orderable;
    this.showTags =
      !!dataView.viewConfiguration?.entryClassificationStyles?.backgroundColor
        ?.allowChanging ||
      !!dataView.viewConfiguration?.entryClassificationStyles?.backgroundColor
        ?.selectedOption?.dataEntry ||
      !!dataView.viewConfiguration?.entryClassificationStyles?.borderColor
        ?.allowChanging ||
      !!dataView.viewConfiguration?.entryClassificationStyles?.borderColor
        ?.selectedOption?.dataEntry ||
      !!dataView.viewConfiguration?.entryClassificationStyles?.tags
        ?.allowChanging ||
      !!dataView.viewConfiguration?.entryClassificationStyles?.tags
        ?.selectedOption?.length;
    this.showDraftEntries =
      dataView.editConfiguration.allowCreateEntryAsDraft ||
      (dataView.editConfiguration?.enabled &&
        dataView.editConfiguration?.supportedOperations?.some(
          (x) =>
            x.operationType ===
              EntryActionOperationEnum.AllowCreateNewEntries &&
            x.allowCreateEntryAsDraft,
        ));
    this.allowCreateInvalidEntry =
      dataView.editConfiguration?.enabled &&
      dataView.editConfiguration?.supportedOperations?.some(
        (x) =>
          x.operationType === EntryActionOperationEnum.AllowCreateNewEntries &&
          x.allowSaveInvalidEntries,
      );
    this.allowQuickCreate =
      dataView.editConfiguration?.enabled &&
      dataView.editConfiguration?.supportedOperations?.some(
        (x) =>
          x.operationType === EntryActionOperationEnum.AllowCreateNewEntries &&
          x.allowQuickCreate,
      );
    this.allowDuplicateEntry =
      dataView.editConfiguration?.enabled &&
      dataView.editConfiguration?.supportedOperations?.some(
        (x) =>
          x.operationType === EntryActionOperationEnum.AllowCreateNewEntries &&
          x.allowDuplicateEntry,
      );
    this.allowEditInvalidEntry =
      dataView.editConfiguration?.enabled &&
      dataView.editConfiguration?.supportedOperations?.some(
        (x) =>
          x.operationType === EntryActionOperationEnum.AllowEditEntries &&
          x.allowSaveInvalidEntries,
      );
  }

  setDefaultGeneralConfig(): void {
    this.viewEntry = true;
    this.addEntry = true;
    this.editEntry = true;
    this.numberOfRecords = true;
    this.addToListOfValues = true;
    this.archiveRecords = true;
    this.archiveEntry = !isCoreRequestEntity(this.dataEntity);
    this.createNewListOfValues = true;
    this.editDataEntity = true;
    this.viewJson = true;
    this.allowFilters = true;
    this.allowFilterCurrentStep = true;
    this.allowFilterPreviousSteps = true;
    this.dashboardConfiguration = {
      enabled: true,
      addSummery: true,
    };
    this.displayActions = true;
    this.showSearch = true;
    this.showFilter = true;
    this.showMetaDataFilter = true;
    this.showDetailsMetaDataFilter = true;
    this.allowChangeSorting = true;
    this.showCurrentUserFilter = true;
    this.showCurrentUserGroupFilter = true;
    this.showExport = true;
    this.showPagination = true;
    this.showSubheader = true;
    this.orderable =
      !isVirtualJoinerEntity(this.dataEntity) &&
      !isVirtualProxyEntity(this.dataEntity);
  }

  /**
   * @TODO Move to view entry ui configuration class
   */
  hasViewPermmission(): boolean {
    return this.dataView.entriesConfiguration.enabled;
  }

  /**
   * @TODO Move to create entry ui configuration class
   */
  hasCreatePermission(): boolean {
    return hasCreatePermission(this.dataView);
  }

  hasArchivePermission(): boolean {
    return hasArchivePermission(this.dataView);
  }

  filterFields(selectedFields: string[]): FieldPresenterModel[] {
    if (!selectedFields) {
      return [];
    }
    function innerFun(
      fields: FieldPresenterModel[],
      blockName?: LocalizedValueModel,
    ): FieldPresenterModel[] {
      const _fields: FieldPresenterModel[] = [];
      fields.forEach((_field) => {
        _field = cloneDeep(_field);
        if (_field.dataType === DataTypeEnum.Block) {
          const _res = innerFun(
            _field.block.internalEntity.fields,
            _field.name,
          );
          const _filteredFields = _res.filter((_inf) =>
            selectedFields.some(
              (_selectedField) => _selectedField === _inf._normalizedName,
            ),
          );
          if (_filteredFields.length) {
            _field.block.internalEntity.fields = [..._filteredFields];
            _fields.push(_field);
          }
        } else if (!blockName) {
          const _fieldExist = selectedFields.some(
            (_f) => _f === _field.normalizedName,
          );
          if (_fieldExist) {
            _fields.push(_field);
          }
        } else {
          _fields.push(_field);
        }
      });
      return _fields;
    }

    return innerFun(this.dataEntity.fields);
  }

  mapDisplayOptions(
    fields: FieldPresenterModel[],
    viewOptions: { [key in keyof typeof FieldViewOptionValueTypeEnum]: string },
  ): FieldPresenterModel[] {
    return this._mapDisplayOptions(fields, viewOptions);
  }

  _mapDisplayOptions(
    fields: FieldPresenterModel[],
    viewOptions: { [key in keyof typeof FieldViewOptionValueTypeEnum]: string },
  ): FieldPresenterModel[] {
    if (fields && fields.length > 0) {
      // tslint:disable-next-line: prefer-for-of
      for (let index = 0; index < fields.length; index++) {
        const field = this.resetOptions(fields[index]);
        if (field.dataType === DataTypeEnum.Block) {
          this._mapDisplayOptions(
            field.block?.internalEntity?.fields,
            viewOptions,
          );
        } else {
          if (
            this.checkNormalizedName(
              viewOptions.showAsTitle,
              field._normalizedName,
            )
          ) {
            field.options.showAsTitle = true;
          }
          if (
            this.checkNormalizedName(
              viewOptions.showAsMainDate,
              field._normalizedName,
            )
          ) {
            field.options.showAsMainDate = true;
          }
          if (
            this.checkNormalizedName(
              viewOptions.showAsSubTitle,
              field._normalizedName,
            )
          ) {
            field.options.showAsSubTitle = true;
          }
          if (
            this.checkNormalizedName(
              viewOptions.showAsEntryPicture,
              field._normalizedName,
            )
          ) {
            field.options.showAsEntryPicture = true;
          }
          if (
            this.checkNormalizedName(
              viewOptions.showAsRelationField,
              field._normalizedName,
            )
          ) {
            field.options.showAsRelationField = true;
          }
          if (
            this.checkNormalizedName(
              viewOptions.showAsCategory,
              field._normalizedName,
            )
          ) {
            field.options.showAsCategory = true;
          }
          if (
            this.checkNormalizedName(
              viewOptions.showAsSubCategory,
              field._normalizedName,
            )
          ) {
            field.options.showAsSubCategory = true;
          } else if (
            this.checkNormalizedName(
              viewOptions.showAsMainPhone,
              field._normalizedName,
            )
          ) {
            field.options.showAsMainPhone = true;
          }
          if (
            this.checkNormalizedName(
              viewOptions.showAsMainNumber,
              field._normalizedName,
            )
          ) {
            field.options.showAsMainNumber = true;
          }
        }
      }
    }
    return fields;
  }

  resetOptions(field: FieldPresenterModel): FieldPresenterModel {
    field.options.showAsTitle = false;
    field.options.showAsSubTitle = false;
    field.options.showAsMainDate = false;
    field.options.showAsEntryPicture = false;
    field.options.showAsCategory = false;
    field.options.showAsMainPhone = false;
    field.options.showAsSubCategory = false;
    field.options.showAsRelationField = false;
    field.options.showAsMainNumber = false;
    // field.options.show
    return field;
  }

  checkNormalizedName(
    normalizedName: string,
    fieldNormalizedName: string,
  ): boolean {
    if (!normalizedName) {
      return null;
    }
    return normalizedName === fieldNormalizedName;
  }

  initEditableFields(): FieldPresenterModel[] {
    if (hasInlineEditPermission(this.dataView)) {
      const fieldNames: Array<string> =
        this._getSupportedOperationConfiguration(
          EntryActionOperationEnum.AllowEditEntries,
        )?.fields;
      return this.filterFields(fieldNames);
    }
    if (
      hasCreatePermission(this.dataView) &&
      this.dataView.editConfiguration?.allowQuickCreate
    ) {
      const fieldNames: Array<string> =
        this._getSupportedOperationConfiguration(
          EntryActionOperationEnum.AllowCreateNewEntries,
        )?.fields;
      return this.filterFields(fieldNames);
    }
    return [];
  }

  initAllowedToEditFields(): FieldPresenterModel[] {
    const fieldNames: Array<string> = this._getSupportedOperationConfiguration(
      EntryActionOperationEnum.AllowEditEntries,
    )?.fields;
    if (fieldNames) {
      return this.filterFields(fieldNames);
    }

    return [];
  }

  protected _getSupportedOperationConfiguration(
    operation: EntryActionOperationEnum,
  ): DataViewEditSupportedOperationModel {
    return this.dataView.editConfiguration?.supportedOperations?.find(
      (opt) => opt.operationType === operation,
    );
  }
}

export abstract class TableViewUIConfiguration
  extends GeneralUIViewConfiguration
  implements EntriesViewTypesUIConfiguration
{
  isScrollable = false;
  display: boolean;
  displyedFields: FieldPresenterModel[];
  selectRecords: boolean;
  allowChangeFields: boolean;
  allowSort: boolean;
  maxDisplayedValues?: TableViewMaxDisplayedValuesConfig;

  constructor(
    dataEntity: DataEntityPresenterModel,
    dataView?: DataViewsPresenterModel,
  ) {
    super(dataView, dataEntity);
  }

  abstract initDisplyedFields(): FieldPresenterModel[];
}

export abstract class KanbanViewUIConfiguration
  extends GeneralUIViewConfiguration
  implements EntriesViewTypesUIConfiguration
{
  display: boolean;
  allowDragAndDrop: boolean;
  displyedFields: FieldPresenterModel[];
  showMetaData: boolean;
  selectRecords: boolean;
  selectedCategories: Array<string>;
  constructor(
    dataEntity: DataEntityPresenterModel,
    dataView?: DataViewsPresenterModel,
  ) {
    super(dataView, dataEntity);
    this.editableFields = getTreeFilteredFields(
      this.editableFields,
      (field: FieldPresenterModel) => {
        return (
          !isFieldBlock(field) ||
          (isFieldBlock(field) && !isFieldRepeatable(field))
        );
      },
      null,
      {
        ignoreBlockWhenFiltering: false,
      },
    );
  }

  abstract getCategoryField(): FieldPresenterModel;
}

export abstract class TreeViewUIConfiguration
  extends GeneralUIViewConfiguration
  implements EntriesViewTypesUIConfiguration
{
  display: boolean;
  displyedFields: FieldPresenterModel[];
  constructor(
    dataEntity: DataEntityPresenterModel,
    dataView?: DataViewsPresenterModel,
  ) {
    super(dataView, dataEntity);
  }
  hideTreeFieldOptions: boolean;
  abstract getTreeReferenceNormalizedName(): FieldPresenterModel;
}

export abstract class BadgeViewUIConfiguration
  extends GeneralUIViewConfiguration
  implements EntriesViewTypesUIConfiguration
{
  display: boolean;
  displyedFields: FieldPresenterModel[];
  showMetaData: boolean;
  selectRecords: boolean;

  constructor(
    dataEntity: DataEntityPresenterModel,
    dataView?: DataViewsPresenterModel,
  ) {
    super(dataView, dataEntity);
  }
}

export abstract class CalendarViewUIConfiguration
  extends GeneralUIViewConfiguration
  implements EntriesViewTypesUIConfiguration
{
  display: boolean;
  displyedFields: FieldPresenterModel[];

  constructor(
    dataEntity: DataEntityPresenterModel,
    dataView?: DataViewsPresenterModel,
  ) {
    super(dataView, dataEntity);
  }
}

export abstract class TimelineViewUIConfiguration
  extends GeneralUIViewConfiguration
  implements EntriesViewTypesUIConfiguration
{
  display: boolean;
  enablePublishing: boolean;
  enableToShowStepName: boolean;
  constructor(dataEntity: DataEntityPresenterModel) {
    super(undefined, dataEntity);
  }
}

export abstract class EditEntryConfiguration extends GeneralUIViewConfiguration {
  displyedMessage: LocalizedValueModel;
  displyedFields: FieldPresenterModel[];
  discussionConfig: DataViewEntriesDiscussionConfiguration;
  historyConfig: DataViewEntryHistoryConfiguration;
  disabledFieldsNames: Array<string>;
  constructor(
    newMode: boolean,
    dataEntity: DataEntityPresenterModel,
    dataView?: DataViewsPresenterModel,
    disabledFieldsNames?: string[],
  ) {
    super(dataView, dataEntity);
    this.displyedFields = this._getDisplayFields(newMode);
    this.discussionConfig = this.initDiscussionConfig();
    this.historyConfig = this.initHistoryConfig();
    this.disabledFieldsNames = disabledFieldsNames || [];
  }

  initDisplayedFields(newMode: boolean): FieldPresenterModel[] {
    return this.dataEntity.fields;
  }

  initDiscussionConfig(): DataViewEntriesDiscussionConfiguration {
    if (this.dataView) {
      return getViewDiscussionConfig(this.dataView);
    }
    return DISCUSSION_DEFAULT_CONFIG;
  }

  initHistoryConfig(): DataViewEntryHistoryConfiguration {
    if (this.dataView) {
      return getViewHistoryConfig(this.dataView);
    }
    return HISTORY_DEFAULT_CONFIG;
  }

  abstract getMessage(operation: EntryActionOperationEnum): LocalizedValueModel;

  private _getDisplayFields(newMode: boolean): FieldPresenterModel[] {
    return getTreeFilteredFields(
      this.initDisplayedFields(newMode),
      (field) => !(newMode && field.options?.autoIncrementOptions?.enabled),
    );
  }

  hasReturnToEntriesList(operation: EntryActionOperationEnum): boolean {
    return true;
  }
  hasReturnToEntryView(operation: EntryActionOperationEnum): boolean {
    return true;
  }
  hasResetFormAction(operation: EntryActionOperationEnum): boolean {
    return false;
  }
}

export abstract class ViewEntryConfiguration extends GeneralUIViewConfiguration {
  displyedFields: FieldPresenterModel[];
  discussionConfig: DataViewEntriesDiscussionConfiguration;
  historyConfig: DataViewEntryHistoryConfiguration;
  supportPaginationBetweenEntries: boolean;
  constructor(
    dataEntity: DataEntityPresenterModel,
    dataView?: DataViewsPresenterModel,
  ) {
    super(dataView, dataEntity);
    this.displyedFields = this.initDisplyedFields();
    this.discussionConfig = this.initDiscussionConfig();
    this.historyConfig = this.initHistoryConfig();
  }

  getRelations(): DataViewRelationModel[] {
    const _relations = this.dataView?.relations;
    return _relations;
  }

  abstract initDisplyedFields(): FieldPresenterModel[];
  initDiscussionConfig(): DataViewEntriesDiscussionConfiguration {
    if (this.dataView) {
      return getViewDiscussionConfig(this.dataView);
    }
    return DISCUSSION_DEFAULT_CONFIG;
  }

  initHistoryConfig(): DataViewEntryHistoryConfiguration {
    if (this.dataView) {
      return getViewHistoryConfig(this.dataView);
    }
    return HISTORY_DEFAULT_CONFIG;
  }

  abstract getMessage(operation: EntryActionOperationEnum): LocalizedValueModel;

  hasReturnToEntriesList(operation: EntryActionOperationEnum): boolean {
    return true;
  }
  hasReturnToEntryView(operation: EntryActionOperationEnum): boolean {
    return true;
  }
  hasResetFormAction(): boolean {
    return false;
  }
}

interface DashboardConfiguration {
  enabled: boolean;
  addSummery?: boolean;
}
// TODO: currently we have no common class for view configuration, I had to make the common functions as utils
// Later we need to reconsider the config classes hierarchy
function getViewDiscussionConfig(
  dataView: DataViewsPresenterModel,
): DataViewEntriesDiscussionConfiguration {
  return {
    enabled: dataView.entriesConfiguration?.discussions?.enabled,
    allowAddComments:
      dataView.entriesConfiguration?.discussions?.allowAddComments,
    showWorkflowComments:
      dataView.entriesConfiguration?.discussions?.showWorkflowComments,
    allowMentions: dataView.entriesConfiguration?.discussions?.allowMentions,
    allowAudience: dataView.entriesConfiguration?.discussions?.enabled,
    allowAddAttachments: {
      enabled:
        !!dataView.entriesConfiguration?.discussions?.allowAddAttachments
          ?.enabled,
      maxSize: dataView.entriesConfiguration?.discussions?.allowAddAttachments
        ?.maxSize
        ? dataView.entriesConfiguration?.discussions?.allowAddAttachments
            ?.maxSize
        : 0,
      allowedExtensions: dataView.entriesConfiguration?.discussions
        ?.allowAddAttachments?.allowedExtensions
        ? dataView.entriesConfiguration?.discussions?.allowAddAttachments
            ?.allowedExtensions
        : [],
    },
  };
}
function getViewHistoryConfig(
  dataView: DataViewsPresenterModel,
): DataViewEntryHistoryConfiguration {
  return {
    enabled: dataView.entriesConfiguration?.history?.enabled,
    showChanger: dataView.entriesConfiguration?.history?.showChanger,
    showChanges: dataView.entriesConfiguration?.history?.showChanges,
  };
}
export abstract class MiniCardViewUIConfiguration
  extends GeneralUIViewConfiguration
  implements EntriesViewTypesUIConfiguration
{
  display: boolean;
  displyedFields: FieldPresenterModel[];

  constructor(
    dataEntity: DataEntityPresenterModel,
    dataView?: DataViewsPresenterModel,
  ) {
    super(dataView, dataEntity);
  }
}
